import { getNostoApi } from '@theme/bt-nosto-search';

/**
 * @typedef {object} NostoPlacementProduct
 * @property {string} [handle]
 * @property {string} [url]
 * @property {string} [name]
 * @property {string} [title]
 * @property {string} [product_id]
 * @property {string} [productId]
 * @property {string} [image_url]
 * @property {string} [imageUrl]
 * @property {string} [thumb_url]
 * @property {string} [image]
 * @property {string|number} [price]
 * @property {string} [price_text]
 * @property {string} [priceText]
 * @property {Record<string, unknown>} [custom_fields]
 * @property {Record<string, unknown>} [customFields]
 */

/**
 * Nosto JSON recommendation fetch. PDP mix & match is the last caller
 * (`bt-pdp-mix-match.js`). Delete this helper (and unused exports here)
 * if that list stops using the JSON API.
 *
 * @param {object} options
 * @param {string} options.placementId
 * @param {string} [options.productId]
 * @param {string} [options.logPrefix]
 */
export async function loadPlacement({ placementId, productId, logPrefix = '[bt-nosto-placement]' }) {
  const api = await getNostoApi(undefined, { requireSearch: false });
  if (!api) return null;

  if (productId && typeof api.defaultSession === 'function') {
    try {
      const viaProduct = await api
        .defaultSession()
        .setResponseMode('JSON_ORIGINAL')
        .viewProduct(productId)
        .setPlacements([placementId])
        .load();
      if (campaignProducts(campaignFromResponse(viaProduct, placementId)).length) {
        return viaProduct;
      }
    } catch (error) {
      console.warn(`${logPrefix} viewProduct failed`, error);
    }
  }

  if (typeof api.createRecommendationRequest !== 'function') return null;

  return api
    .createRecommendationRequest({ includeTagging: true })
    .setResponseMode('JSON_ORIGINAL')
    .setElements([placementId])
    .load();
}

/**
 * @param {any} response
 * @param {string} [placementId]
 */
export function campaignFromResponse(response, placementId) {
  const recs = response?.recommendations || {};
  if (placementId && recs[placementId]) return recs[placementId];

  for (const campaign of Object.values(recs)) {
    if (!campaign || typeof campaign === 'string') continue;
    if (placementId && (campaign.element === placementId || campaign.div_id === placementId)) {
      return campaign;
    }
  }

  return placementId ? null : Object.values(recs)[0] || null;
}

/**
 * @param {any} campaign
 * @returns {NostoPlacementProduct[]}
 */
export function campaignProducts(campaign) {
  if (!campaign || typeof campaign === 'string') return [];
  if (Array.isArray(campaign.products)) return campaign.products;
  if (Array.isArray(campaign)) return campaign;
  return [];
}

/**
 * @param {NostoPlacementProduct} product
 */
export function productHandle(product) {
  if (product?.handle) return String(product.handle).replace(/^\/+|\/+$/g, '');
  return handleFromProductUrl(product?.url);
}

/**
 * @param {NostoPlacementProduct} product
 */
export function productImage(product) {
  return String(product.image_url || product.imageUrl || product.thumb_url || product.image || '');
}

/**
 * @param {string} [url]
 */
export function handleFromProductUrl(url) {
  if (!url) return '';
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\/products\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/**
 * @param {string} url
 */
export function storefrontPath(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

/**
 * @param {string} url
 * @param {number} [width]
 */
export function sizedImage(url, width = 136) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.hostname.includes('cdn.shopify.com') && !parsed.searchParams.has('width')) {
      parsed.searchParams.set('width', String(width));
    }
    return parsed.href;
  } catch {
    return url;
  }
}

/**
 * Click attribution can wait. It must not sit in front of the first paint.
 * @param {HTMLElement} root
 * @param {any} campaign
 */
export function attributeClicks(root, campaign) {
  if (!root || !campaign || typeof campaign !== 'object') return;
  const nostojs = window.nostojs;
  if (typeof nostojs !== 'function') return;
  nostojs((api) => {
    if (typeof api.attributeProductClicksInCampaign === 'function') {
      api.attributeProductClicksInCampaign(root, campaign);
    }
  });
}
